import type { TextureManager } from '../../../managers/texture-manager';
import type { ModelManager } from '../../../managers/model-manager';
import type { CutoutManager } from '../../../managers/cutout-manager';
import type { RenderKitchenProps } from '../../../../types/react-props';
import type { QualitySettings } from '../../../../hooks/use-three-kitchen/quality';

/**
 * Controls whether alternative textures (surround, countertop, floor, sky variants)
 * are background-preloaded after the scene builds.
 *
 * - 'all'      — preload all remaining texture variants immediately after build (~64MB download)
 * - 'deferred' — wait for the browser to be idle (requestIdleCallback / 3s fallback)
 *                then preload in small batches to avoid blocking the main thread (default)
 * - 'none'     — skip background preloads entirely (conserves bandwidth on mobile/limited connections)
 */
export type PreloadStrategy = 'all' | 'deferred' | 'none';

/**
 * Full kitchen input data from RenderKitchenProps
 */
export interface KitchenBlueprintInput {
	/** Kitchen data from blueprint library */
	kitchen: RenderKitchenProps['kitchen'];
	/** Controller options */
	controller?: RenderKitchenProps['controller'];
	/** Floor texture key */
	floorKey?: string;
	/** Sky texture key */
	skyKey?: string;
	/** Background color */
	backgroundColor?: string;
}

/**
 * Configuration for KitchenSceneBuilder
 */
export interface KitchenSceneBuilderConfig {
	/** Base path for assets */
	basePath: string;
	/** Render quality (0-10) */
	renderQuality: number;
	/** Texture quality tier. null = follow renderQuality. 0 = skip sky texture. */
	textureQuality: number | null;
	/** Quality settings */
	qualitySettings: QualitySettings | null;
	/** Texture manager reference */
	textureManager: TextureManager | null;
	/** Model manager reference */
	modelManager: ModelManager | null;
	/** Cutout manager reference */
	cutoutManager: CutoutManager | null;
	/** Callback when sky texture is loaded - allows immediate background setting */
	onSkyReady?: (skyKey: string) => void;
	/**
	 * Controls background preloading of texture variants after scene build.
	 * Defaults to 'deferred' — waits until the browser is idle, then preloads
	 * in small batches so all textures are cached for instant switching.
	 */
	preloadStrategy?: PreloadStrategy;
	/** Returns the engine's current build generation token. Async continuations
	 *  compare against their captured value to bail out when superseded. */
	getBuildGeneration?: () => number;
}

/**
 * Default configuration for KitchenSceneBuilder
 */
export const DEFAULT_KITCHEN_CONFIG: KitchenSceneBuilderConfig = {
	basePath: '',
	renderQuality: 7,
	textureQuality: null,
	qualitySettings: null,
	textureManager: null,
	modelManager: null,
	cutoutManager: null,
	preloadStrategy: 'deferred'
};
